import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {type IUserStore} from "data/stores/user/user.store";
import React from "react";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable} from "mobx";
import {RequestState} from "data/enums";
import type {AxiosError} from "axios";
import type {IApiResponse} from "data/services/http";
import type {IForgotPasswordPayload} from "data/providers/api/password.api.provider";
import {extractErrorMessage} from "data/utils";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {eq, get} from "lodash";

interface IForgotPasswordForm extends HTMLFormElement {
	email: HTMLInputElement;
}

export interface IForgotPasswordController extends ViewController {
	handleFormSubmit: (event: React.SyntheticEvent<IForgotPasswordForm>) => void;
	forgotPassword: (params: IForgotPasswordPayload) => void;
	handleFormOnChange: (event: React.FormEvent<HTMLFormElement>) => void;

	get i18n(): ILocalizationStore;
	get error(): string | undefined;
	get isSuccess(): boolean | undefined;
	get isFormDisabled(): boolean;
	get isFormSubmitDisabled(): boolean;
	get email(): string | undefined;
}

@injectable()
export class ForgotPasswordController implements IForgotPasswordController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _error?: string = undefined;
	@observable email: string = "";

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get error() {
		return this._error;
	}

	get isSuccess() {
		return this._requestState === RequestState.SUCCESS;
	}

	get isFormDisabled() {
		return this._requestState === RequestState.PENDING;
	}

	get isFormSubmitDisabled() {
		return eq(this.email.length, 0);
	}

	@action handleFormOnChange = (event: React.FormEvent<HTMLFormElement>) => {
		this._error = undefined;
		this._requestState = RequestState.IDLE;

		const value = get(event.target, "value", "");
		const name = get(event.target, "name", "");

		if (eq(name, "email")) {
			this.email = value;
		}
	};

	@action private onError = (error: AxiosError<IApiResponse>) => {
		this._requestState = RequestState.ERROR;
		this._error = extractErrorMessage(error);
	};

	@action private onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
	};

	@action forgotPassword(payload: IForgotPasswordPayload) {
		this._requestState = RequestState.PENDING;

		void this._userStore.forgotPassword(payload).then(this.onSuccess).catch(this.onError);
	}

	@action handleFormSubmit = (event: React.SyntheticEvent<IForgotPasswordForm>) => {
		event.preventDefault();
		const {email} = event.currentTarget;

		if (!email.checkValidity()) {
			this._error = "Please provide a valid email address";
			return;
		}

		void this.forgotPassword({
			email: email.value,
		});
	};

	dispose(): void {
		return;
	}

	init(): void {
		return;
	}
}
